"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const constants_1 = require("./constants");
const TabbingHoc = ({ containerRef, children, searchable, creatable, scrollBarRef, }) => {
    const focusableElements = (0, react_1.useCallback)(() => [
        ...containerRef?.current?.querySelectorAll(`*[${constants_1.TABBING_ATTRIBUTE}=${constants_1.TABBING_KEY}]`),
    ], [containerRef.current]);
    const moveFocus = (0, react_1.useCallback)(({ tabIndex, direction, isClick, }) => {
        const allElements = focusableElements();
        const focusedElement = allElements.findIndex((element) => Number(element.getAttribute(constants_1.TAB_INDEX_ATTRIBUTE)) === tabIndex);
        const nextOption = allElements[focusedElement + 1];
        const previousOption = allElements[focusedElement - 1];
        if (isClick) {
            (containerRef?.current?.querySelector(`[tabindex="${tabIndex}"]`)).click();
            if (nextOption) {
                nextOption.focus();
            }
            else if (previousOption) {
                previousOption.focus();
            }
        }
        else if (direction === constants_1.DIRECTIONS.NEXT && nextOption) {
            nextOption.focus();
        }
        else if (direction === constants_1.DIRECTIONS.PREVIOUS && previousOption) {
            previousOption.focus();
        }
    }, [containerRef.current]);
    const handleKeyDownEvent = (0, react_1.useCallback)((event) => {
        const target = event.target;
        const tabIndex = Number(target.getAttribute(constants_1.TAB_INDEX_ATTRIBUTE));
        if ((event.shiftKey && event.key === constants_1.TAB_KEY) ||
            event.key === constants_1.ARROW_UP_KEY) {
            event.preventDefault();
            moveFocus({ tabIndex, direction: constants_1.DIRECTIONS.PREVIOUS });
        }
        else if (event.key === constants_1.TAB_KEY || event.key === constants_1.ARROW_DOWN_KEY) {
            event.preventDefault();
            moveFocus({ tabIndex, direction: constants_1.DIRECTIONS.NEXT });
        }
        const isCreateOptionFocused = target.getAttribute(constants_1.ROLE_ATTRIBUTE) === constants_1.CREATE_OPTION_ROLE;
        if (event.key === constants_1.ENTER_KEY) {
            if (!isCreateOptionFocused) {
                moveFocus({ tabIndex, isClick: true });
            }
            else {
                // get a time to update options and then scroll to bottom
                setTimeout(() => {
                    scrollBarRef.current.scrollToBottom();
                }, 0);
            }
        }
    }, [moveFocus, scrollBarRef.current]);
    const handleMouseOverEvent = (0, react_1.useCallback)((event) => {
        const target = event.target;
        if (target?.getAttribute(constants_1.TABBING_ATTRIBUTE) === constants_1.TABBING_KEY) {
            document.activeElement?.blur();
        }
    }, []);
    (0, react_1.useEffect)(() => {
        if (containerRef.current) {
            const divElements = focusableElements();
            if (searchable || creatable) {
                const attributeForSearch = searchable
                    ? constants_1.SEARCH_INPUT_ROLE
                    : constants_1.CREATE_OPTION_ROLE;
                divElements
                    .find((element) => element.getAttribute(constants_1.ROLE_ATTRIBUTE) === attributeForSearch)
                    ?.focus();
            }
        }
    }, []);
    (0, react_1.useEffect)(() => {
        if (containerRef.current) {
            const divElements = focusableElements();
            divElements.forEach((el, index) => el.setAttribute(constants_1.TAB_INDEX_ATTRIBUTE, String(index + 1)));
            const isSearchInputFocused = document.activeElement?.getAttribute(constants_1.ROLE_ATTRIBUTE) ===
                constants_1.SEARCH_INPUT_ROLE;
            const isOptionFocused = document.activeElement?.getAttribute(constants_1.TABBING_ATTRIBUTE) === constants_1.TABBING_KEY;
            const isCreateOptionFocused = document.activeElement?.getAttribute(constants_1.ROLE_ATTRIBUTE) ===
                constants_1.CREATE_OPTION_ROLE;
            if (!isSearchInputFocused && !isOptionFocused && !isCreateOptionFocused) {
                divElements[0]?.focus();
            }
        }
    }, [children]);
    (0, react_1.useEffect)(() => {
        const { current } = containerRef;
        current.addEventListener('keydown', handleKeyDownEvent);
        current.addEventListener('mouseover', handleMouseOverEvent);
        return () => {
            current.removeEventListener('keydown', handleKeyDownEvent);
            current.removeEventListener('mouseover', handleMouseOverEvent);
        };
    }, [handleKeyDownEvent, handleMouseOverEvent]);
    return children;
};
exports.default = TabbingHoc;
